import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  ContentCopy,
  OpenInNew as OpenInNewIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';
import { normalizePricingRow } from '../../utils/pricing';

const ManagerInvoices = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0); // 0: All, 1: Paid, 2: Submitted, 3: Pending
  const [openDialog, setOpenDialog] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [formData, setFormData] = useState({
    customer: '',
    jobs: [], // Array of { job: '', delivery_fee: '' }
    tax: '',
    credit_card_surcharge_percent: '3',
    due_date: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [successSeverity, setSuccessSeverity] = useState('success');
  const [apiError, setApiError] = useState('');
  const [sendingInvoiceId, setSendingInvoiceId] = useState(null);

  const clearSuccessBanner = () => {
    setSuccess('');
    setSuccessSeverity('success');
  };
  const [currentPricing, setCurrentPricing] = useState(null);
  const [jobDetailsMap, setJobDetailsMap] = useState({}); // Map of jobId -> full job details with sub_jobs

  useEffect(() => {
    fetchInvoices();
    fetchCustomers();
    fetchJobs();
  }, []);

  // Get set of job IDs that are already associated with invoices
  const getInvoicedJobIds = () => {
    const invoicedJobIds = new Set();
    invoices.forEach(invoice => {
      if (invoice.jobs && Array.isArray(invoice.jobs)) {
        invoice.jobs.forEach(invoiceJob => {
          // Handle different invoice job formats
          if (invoiceJob.job_id) {
            invoicedJobIds.add(invoiceJob.job_id);
          } else if (invoiceJob.job && typeof invoiceJob.job === 'string') {
            invoicedJobIds.add(invoiceJob.job);
          } else if (invoiceJob.job && invoiceJob.job.id) {
            invoicedJobIds.add(invoiceJob.job.id);
          }
        });
      }
    });
    // Also check jobs that have invoice_number set directly
    jobs.forEach(job => {
      if (job.invoice_number) {
        invoicedJobIds.add(job.id);
      }
    });
    return invoicedJobIds;
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/invoices/');
      
      // Handle different response formats
      let invoicesData = [];
      if (Array.isArray(response.data)) {
        // Direct array response
        invoicesData = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        // Paginated response (Django REST Framework default pagination)
        invoicesData = response.data.results;
      } else if (response.data && typeof response.data === 'object') {
        // Try to extract array from response object
        const arrayValue = Object.values(response.data).find(val => Array.isArray(val));
        invoicesData = arrayValue || [];
      }
      
      console.log('Fetched invoices:', invoicesData.length);
      setInvoices(invoicesData);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
      }
      setInvoices([]);
      setApiError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getFilteredInvoices = () => {
    // Ensure invoices is always an array
    if (!Array.isArray(invoices)) {
      return [];
    }
    
    if (tabValue === 0) {
      // All invoices
      return invoices;
    } else if (tabValue === 1) {
      // Paid invoices
      return invoices.filter((invoice) => invoice?.status === 'paid');
    } else if (tabValue === 2) {
      // Submitted invoices
      return invoices.filter((invoice) => invoice?.status === 'submitted');
    } else if (tabValue === 3) {
      // Pending invoices
      return invoices.filter((invoice) => invoice?.status === 'pending');
    }
    return invoices;
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/managers/customers/');
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await api.get('/managers/jobs/');
      // Get all jobs - we'll filter by completed status and customer in the dropdown
      const allJobs = Array.isArray(response.data) ? response.data : [];
      setJobs(allJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]);
    }
  };

  const fetchCurrentPricing = async () => {
    try {
      const response = await api.get('/pricing/pricing/current/');
      setCurrentPricing(normalizePricingRow(response.data));
    } catch (error) {
      console.error('Error fetching current pricing:', error);
      // If no current pricing, try to get pricing for today
      try {
        const today = new Date().toISOString().split('T')[0];
        const response = await api.get(`/pricing/pricing/for_date/?date=${today}`);
        setCurrentPricing(normalizePricingRow(response.data));
      } catch (err) {
        console.error('No pricing found for today:', err);
        setCurrentPricing(null);
      }
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setError('');
    clearSuccessBanner();
    setFormData({
      customer: '',
      jobs: [{ job: '', delivery_fee: '' }], // Start with one empty job
      tax: '',
      credit_card_surcharge_percent: '3',
      due_date: '',
    });
    fetchCurrentPricing(); // Fetch current pricing when dialog opens
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
    clearSuccessBanner();
    setJobDetailsMap({}); // Clear job details when dialog closes
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If customer changes, clear jobs
    if (name === 'customer') {
      setFormData(prev => ({
        ...prev,
        customer: value,
        jobs: [{ job: '', delivery_fee: '' }], // Reset to one empty job
      }));
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleJobChange = async (index, field, value) => {
    const newJobs = [...formData.jobs];
    newJobs[index] = {
      ...newJobs[index],
      [field]: value,
    };
    setFormData({
      ...formData,
      jobs: newJobs,
    });
    
    // If a job is selected, fetch full job details including sub_jobs
    if (field === 'job' && value) {
      try {
        const response = await api.get(`/jobs/${value}/`);
        setJobDetailsMap(prev => ({
          ...prev,
          [value]: response.data
        }));
      } catch (error) {
        console.error('Error fetching job details:', error);
        // If fetch fails, use the job from the jobs list
        const jobFromList = jobs.find(j => j.id === value);
        if (jobFromList) {
          setJobDetailsMap(prev => ({
            ...prev,
            [value]: jobFromList
          }));
        }
      }
    }
  };

  const addJob = () => {
    setFormData({
      ...formData,
      jobs: [...formData.jobs, { job: '', delivery_fee: '' }],
    });
  };

  const removeJob = (index) => {
    if (formData.jobs.length > 1) {
      const newJobs = formData.jobs.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        jobs: newJobs,
      });
    }
  };

  const calculateTotal = () => {
    const deliveryFeesTotal = formData.jobs.reduce((sum, job) => {
      return sum + (parseFloat(job.delivery_fee) || 0);
    }, 0);
    
    // Calculate fuel costs based on delivered quantities
    let fuelCostsTotal = 0;
    if (currentPricing) {
      formData.jobs.forEach((jobItem) => {
        const selectedJob = jobs.find(j => j.id === jobItem.job);
        if (selectedJob) {
          // Use delivered quantities (use delivered_def only, no fallback to ordered)
          const dieselQty = parseFloat(selectedJob.delivered_diesel) || parseFloat(selectedJob.diesel_quantity) || 0;
          const defQty = parseFloat(selectedJob.delivered_def) || 0;
          
          if (dieselQty > 0) {
            fuelCostsTotal += dieselQty * parseFloat(currentPricing.diesel_price_per_liter);
          }
          if (defQty > 0) {
            fuelCostsTotal += defQty * parseFloat(currentPricing.def_price_per_liter);
          }
        }
      });
    }
    
    const tax = parseFloat(formData.tax) || 0;
    const subtotal = deliveryFeesTotal + fuelCostsTotal;
    const base = subtotal + tax;
    const pct = parseFloat(formData.credit_card_surcharge_percent);
    const surchargePct = Number.isFinite(pct) && pct >= 0 ? pct : 3;
    const surcharge = Math.round(base * (surchargePct / 100) * 100) / 100;
    return base + surcharge;
  };

  const handleSubmit = async () => {
    setError('');
    clearSuccessBanner();

    // Validate required fields
    if (!formData.customer || !formData.due_date) {
      setError('Please fill in Customer and Due Date');
      return;
    }

    // Validate jobs
    if (!formData.jobs || formData.jobs.length === 0) {
      setError('Please add at least one job');
      return;
    }

    // Validate each job has both job and delivery_fee (delivery_fee can be 0.00)
    const invalidJobs = formData.jobs.filter(j => !j.job || j.delivery_fee === '' || j.delivery_fee === null || j.delivery_fee === undefined || isNaN(parseFloat(j.delivery_fee)) || parseFloat(j.delivery_fee) < 0);
    if (invalidJobs.length > 0) {
      setError('Please select a job and enter a valid delivery fee (can be 0.00) for all jobs');
      return;
    }

    try {
      const payload = {
        customer: formData.customer,
        jobs: formData.jobs.map(j => ({
          job: j.job,
          delivery_fee: parseFloat(j.delivery_fee),
        })),
        tax: parseFloat(formData.tax) || 0,
        credit_card_surcharge_percent: (() => {
          const p = parseFloat(formData.credit_card_surcharge_percent);
          return Number.isFinite(p) && p >= 0 ? p : 3;
        })(),
        due_date: formData.due_date,
      };

      const res = await api.post('/invoices/', payload);
      setSuccessSeverity('success');
      setSuccess(
        res.data?.invoice_number
          ? `Invoice ${res.data.invoice_number} created. Use Send Invoice when ready to email the customer.`
          : 'Invoice created successfully. Use Send Invoice when ready to email the customer.'
      );
      fetchInvoices();
      setTimeout(() => {
        handleCloseDialog();
      }, 1500);
    } catch (error) {
      console.error('Error creating invoice:', error);
      const errorData = error.response?.data;
      let errorMessage = 'Failed to create invoice';
      
      if (errorData) {
        if (errorData.jobs && Array.isArray(errorData.jobs)) {
          // Handle array of errors
          const firstError = errorData.jobs[0];
          if (typeof firstError === 'string') {
            errorMessage = firstError;
          } else if (typeof firstError === 'object' && firstError !== null) {
            // Extract error from object (e.g., {job: "error message"} or {job: ["error message"]})
            let errorText = null;
            
            // Check for job field error (could be string or array)
            if (firstError.job) {
              if (Array.isArray(firstError.job)) {
                errorText = firstError.job[0];
              } else if (typeof firstError.job === 'string') {
                errorText = firstError.job;
              }
              // If job is an object, it's likely the job data itself, not an error - skip it
            }
            
            // Fallback to other error fields
            if (!errorText) {
              if (firstError.non_field_errors) {
                errorText = Array.isArray(firstError.non_field_errors) 
                  ? firstError.non_field_errors[0] 
                  : firstError.non_field_errors;
              } else if (firstError.delivery_fee) {
                errorText = Array.isArray(firstError.delivery_fee) 
                  ? firstError.delivery_fee[0] 
                  : firstError.delivery_fee;
              } else if (firstError[0]) {
                errorText = firstError[0];
              }
            }
            
            // Final fallback - ensure we have a string
            if (!errorText || (typeof errorText === 'object' && errorText !== null)) {
              errorText = 'Validation error for job';
            }
            
            errorMessage = typeof errorText === 'string' ? errorText : String(errorText);
          } else {
            errorMessage = String(firstError);
          }
        } else if (errorData.jobs) {
          // Handle case where errorData.jobs is an object (e.g., {job: "error message"})
          if (typeof errorData.jobs === 'string') {
            errorMessage = errorData.jobs;
          } else if (typeof errorData.jobs === 'object' && errorData.jobs !== null) {
            // Extract error message from object
            const jobError = errorData.jobs.job || errorData.jobs.non_field_errors || errorData.jobs[0];
            if (Array.isArray(jobError)) {
              errorMessage = jobError[0] || 'Validation error';
            } else {
              errorMessage = typeof jobError === 'string' ? jobError : String(jobError || 'Validation error');
            }
          } else {
            errorMessage = String(errorData.jobs || 'Validation error');
          }
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' ? errorData.error : String(errorData.error);
        } else if (errorData.detail) {
          errorMessage = typeof errorData.detail === 'string' ? errorData.detail : String(errorData.detail);
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else {
          // Try to extract any error message from the object
          const errorKeys = Object.keys(errorData);
          if (errorKeys.length > 0) {
            const firstKey = errorKeys[0];
            const firstValue = errorData[firstKey];
            if (Array.isArray(firstValue)) {
              errorMessage = `${firstKey}: ${firstValue[0]}`;
            } else if (typeof firstValue === 'string') {
              errorMessage = `${firstKey}: ${firstValue}`;
            } else {
              errorMessage = `${firstKey}: ${String(firstValue)}`;
            }
          }
        }
      }
      
      setError(errorMessage);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'default',
      submitted: 'warning',
      paid: 'success',
      partial: 'info',
      overdue: 'error',
    };
    return colors[status] || 'default';
  };

  const handleStatusUpdate = async (invoiceId, newStatus) => {
    try {
      setError('');
      await api.patch(`/invoices/${invoiceId}/`, { status: newStatus });
      setSuccessSeverity('success');
      setSuccess(`Invoice status updated to ${newStatus}`);
      fetchInvoices(); // Refresh the list
      setTimeout(() => clearSuccessBanner(), 3000);
    } catch (error) {
      console.error('Error updating invoice status:', error);
      const errorMessage = error.response?.data?.status?.[0] || 
                          error.response?.data?.detail || 
                          'Failed to update invoice status';
      setError(errorMessage);
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      setSendingInvoiceId(invoiceId);
      setError('');
      const res = await api.post(`/invoices/${invoiceId}/send/`);
      if (res.data?.email_sent === false) {
        setSuccessSeverity('warning');
        setSuccess(res.data?.email_notice || res.data?.message || 'Email was not delivered to the customer.');
      } else {
        setSuccessSeverity('success');
        setSuccess(res.data?.message || 'Invoice sent to customer.');
      }
      setTimeout(() => clearSuccessBanner(), 4000);
    } catch (err) {
      console.error('Error sending invoice:', err);
      const msg =
        err.response?.data?.error ||
        err.response?.data?.email_notice ||
        err.response?.data?.detail ||
        'Failed to send invoice to customer.';
      setError(msg);
    } finally {
      setSendingInvoiceId(null);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice? This will dissociate all jobs from the invoice.')) {
      return;
    }
    
    try {
      setError('');
      clearSuccessBanner();
      await api.delete(`/invoices/${invoiceId}/`);
      setSuccessSeverity('success');
      setSuccess('Invoice deleted successfully');
      fetchInvoices();
      setTimeout(() => clearSuccessBanner(), 3000);
    } catch (error) {
      console.error('Error deleting invoice:', error);
      const errorData = error.response?.data;
      const errorMessage = errorData?.detail || errorData?.error || 'Failed to delete invoice';
      setError(errorMessage);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Invoices</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
            Create Invoice
          </Button>
        </Box>
        {success && (
          <Alert severity={successSeverity} sx={{ mb: 2 }} onClose={clearSuccessBanner}>
            {success}
          </Alert>
        )}
        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError('')}>
            {apiError}
          </Alert>
        )}
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="All Invoices" />
          <Tab label="Paid" />
          <Tab label="Submitted" />
          <Tab label="Pending" />
        </Tabs>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice Number</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell sx={{ minWidth: 200 }}>Public pay link</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                const filtered = getFilteredInvoices();
                if (!Array.isArray(filtered) || filtered.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No invoices found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                }
                return filtered.map((invoice) => (
                  <TableRow key={invoice?.id || Math.random()}>
                    <TableCell>{invoice?.invoice_number || 'N/A'}</TableCell>
                    <TableCell>{invoice?.customer_name || 'Unknown'}</TableCell>
                    <TableCell>${invoice?.amount || '0.00'}</TableCell>
                    <TableCell>${invoice?.total_amount || '0.00'}</TableCell>
                    <TableCell>
                      <Chip
                        label={invoice?.status || 'unknown'}
                        color={getStatusColor(invoice?.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {invoice?.due_date 
                        ? new Date(invoice.due_date).toLocaleDateString() 
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {invoice?.public_pay_url ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            maxWidth: 280,
                          }}
                        >
                          <TextField
                            size="small"
                            fullWidth
                            value={invoice.public_pay_url}
                            InputProps={{
                              readOnly: true,
                              sx: { fontSize: '0.75rem', fontFamily: 'monospace' },
                            }}
                            onFocus={(e) => e.target.select()}
                          />
                          <Tooltip title="Copy link">
                            <IconButton
                              size="small"
                              aria-label="Copy public pay link"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(invoice.public_pay_url);
                                  setSuccess('Public payment link copied to clipboard.');
                                  setSuccessSeverity('success');
                                } catch {
                                  setSuccess('Could not copy. Select the text in the field and copy manually.');
                                  setSuccessSeverity('warning');
                                }
                              }}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Open pay page">
                            <IconButton
                              size="small"
                              component="a"
                              href={invoice.public_pay_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label="Open public pay page"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Not available
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={
                            sendingInvoiceId === invoice.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <EmailIcon />
                            )
                          }
                          onClick={() => handleSendInvoice(invoice.id)}
                          disabled={sendingInvoiceId === invoice.id}
                        >
                          Send Invoice
                        </Button>
                        {invoice?.pdf_file_url && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={async () => {
                              try {
                                const response = await api.get(`/invoices/${invoice.id}/download_pdf/`, {
                                  responseType: 'blob',
                                });
                                // Create blob URL and download
                                const blob = new Blob([response.data], { type: 'application/pdf' });
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = `${invoice.invoice_number}.pdf`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                              } catch (error) {
                                console.error('Error downloading PDF:', error);
                                alert('Failed to download PDF. Please try again.');
                              }
                            }}
                          >
                            Download PDF
                          </Button>
                        )}
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={invoice?.status || 'pending'}
                            onChange={(e) => handleStatusUpdate(invoice.id, e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="submitted">Submitted</MenuItem>
                            <MenuItem value="paid">Paid</MenuItem>
                            <MenuItem value="partial">Partial</MenuItem>
                            <MenuItem value="overdue">Overdue</MenuItem>
                          </Select>
                        </FormControl>
                        {/* Only show delete button for invoices with deletable statuses */}
                        {['submitted', 'pending', 'overdue'].includes(invoice?.status) && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteInvoice(invoice.id)}
                            title="Delete Invoice"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>Create Invoice</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth required>
                <InputLabel>Customer</InputLabel>
                <Select
                  name="customer"
                  value={formData.customer}
                  onChange={handleChange}
                  label="Customer"
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.company_name || customer.name || 'Unknown'} ({customer.email || 'N/A'})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">Jobs with Delivery Fees</Typography>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addJob}
                    size="small"
                    variant="outlined"
                  >
                    Add Job
                  </Button>
                </Box>
                
                {!currentPricing && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Pricing information is not available. Please set pricing in the Dashboard.
                  </Alert>
                )}
                
                {formData.jobs.map((jobItem, index) => {
                  // Get available jobs for this customer
                  const customerId = formData.customer;
                  const invoicedJobIds = getInvoicedJobIds();
                  const availableJobs = customerId ? jobs.filter((job) => {
                    const jobCustomerId = typeof job.customer === 'string' 
                      ? job.customer 
                      : job.customer?.id || job.customer;
                    const isCustomerMatch = String(jobCustomerId) === String(customerId);
                    const isCompleted = job.job_status === 'complete';
                    const isNotInvoiced = !invoicedJobIds.has(job.id);
                    const isNotInCurrentForm = !formData.jobs.some((j, i) => i !== index && j.job === job.id);
                    return isCustomerMatch && isCompleted && isNotInvoiced && isNotInCurrentForm;
                  }) : [];

                  // Find the selected job to get quantities
                  // Use jobDetailsMap if available (has full details with sub_jobs), otherwise use jobs list
                  const selectedJob = jobDetailsMap[jobItem.job] || jobs.find(j => j.id === jobItem.job);
                  // Use delivered quantities for pricing (fallback to ordered if not delivered yet)
                  const dieselQty = selectedJob ? (parseFloat(selectedJob.delivered_diesel) || parseFloat(selectedJob.diesel_quantity) || 0) : 0;
                  const defQty = selectedJob ? (parseFloat(selectedJob.delivered_def) || parseFloat(selectedJob.def_quantity) || 0) : 0;
                  // Also get ordered quantities for display
                  const orderedDieselQty = selectedJob ? (parseFloat(selectedJob.diesel_quantity) || 0) : 0;
                  const orderedDefQty = selectedJob ? (parseFloat(selectedJob.def_quantity) || 0) : 0;
                  
                  // Calculate prices if pricing is available (using delivered quantities)
                  const dieselPrice = currentPricing && dieselQty > 0 
                    ? (dieselQty * parseFloat(currentPricing.diesel_price_per_liter)).toFixed(2)
                    : null;
                  const defPrice = currentPricing && defQty > 0
                    ? (defQty * parseFloat(currentPricing.def_price_per_liter)).toFixed(2)
                    : null;
                  const totalFuelPrice = dieselPrice && defPrice
                    ? (parseFloat(dieselPrice) + parseFloat(defPrice)).toFixed(2)
                    : dieselPrice || defPrice || null;

                  return (
                    <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <FormControl fullWidth required sx={{ flex: 2 }}>
                          <InputLabel>Job {index + 1}</InputLabel>
                          <Select
                            value={jobItem.job}
                            onChange={(e) => handleJobChange(index, 'job', e.target.value)}
                            label={`Job ${index + 1}`}
                            required
                          >
                            <MenuItem value="">
                              <em>Select a completed job</em>
                            </MenuItem>
                            {availableJobs.map((job) => (
                              <MenuItem key={job.id} value={job.id}>
                                {job.job_location || 'N/A'} - 
                                {job.diesel_quantity > 0 && ` ${job.diesel_quantity} gal Diesel`}
                                {job.diesel_quantity > 0 && job.def_quantity > 0 && ' /'}
                                {job.def_quantity > 0 && ` ${job.def_quantity} gal DEF`}
                                {(!job.diesel_quantity || job.diesel_quantity === 0) && (!job.def_quantity || job.def_quantity === 0) && ' 0 gal'}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        
                        <TextField
                          label="Delivery Fee"
                          type="number"
                          value={jobItem.delivery_fee}
                          onChange={(e) => handleJobChange(index, 'delivery_fee', e.target.value)}
                          required
                          sx={{ flex: 1 }}
                          inputProps={{ step: '0.01', min: '0' }}
                        />
                        
                        <IconButton
                          onClick={() => removeJob(index)}
                          disabled={formData.jobs.length === 1}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      
                      {/* Display all deliveries: Main job first, then sub-jobs */}
                      {selectedJob && currentPricing && (
                        <Box sx={{ 
                          pl: 2, 
                          pr: 2, 
                          py: 1.5, 
                          mt: 1,
                          bgcolor: 'primary.light', 
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'primary.main'
                        }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.dark' }}>
                            All Deliveries for This Job
                          </Typography>
                          
                          {/* Calculate main job quantities (by subtracting sub-job totals from delivery_completion totals) */}
                          {(() => {
                            let subJobDieselTotal = 0;
                            let subJobDefTotal = 0;
                            const hasSubJobs = selectedJob.delivery_completion && selectedJob.delivery_completion.sub_jobs && Array.isArray(selectedJob.delivery_completion.sub_jobs) && selectedJob.delivery_completion.sub_jobs.length > 0;
                            
                            if (hasSubJobs) {
                              selectedJob.delivery_completion.sub_jobs.forEach(subJob => {
                                subJobDieselTotal += parseFloat(subJob.diesel_delivered) || 0;
                                subJobDefTotal += parseFloat(subJob.def_delivered) || 0;
                              });
                            }
                            
                            const mainJobDiesel = hasSubJobs 
                              ? Math.max(0, parseFloat(selectedJob.delivery_completion?.diesel_delivered || 0) - subJobDieselTotal)
                              : (parseFloat(selectedJob.delivered_diesel) || parseFloat(selectedJob.diesel_quantity) || 0);
                            const mainJobDef = hasSubJobs
                              ? Math.max(0, parseFloat(selectedJob.delivery_completion?.def_delivered || 0) - subJobDefTotal)
                              : (parseFloat(selectedJob.delivered_def) || parseFloat(selectedJob.def_quantity) || 0);
                            
                            const mainDieselPrice = currentPricing && mainJobDiesel > 0 
                              ? (mainJobDiesel * parseFloat(currentPricing.diesel_price_per_liter)).toFixed(2)
                              : null;
                            const mainDefPrice = currentPricing && mainJobDef > 0
                              ? (mainJobDef * parseFloat(currentPricing.def_price_per_liter)).toFixed(2)
                              : null;
                            const mainTotalFuelPrice = mainDieselPrice && mainDefPrice
                              ? (parseFloat(mainDieselPrice) + parseFloat(mainDefPrice)).toFixed(2)
                              : mainDieselPrice || mainDefPrice || null;
                            
                            const mainJobAddress = selectedJob.delivery_address 
                              ? `${selectedJob.delivery_address}${selectedJob.delivery_city ? `, ${selectedJob.delivery_city}` : ''}${selectedJob.delivery_state ? `, ${selectedJob.delivery_state}` : ''}${selectedJob.delivery_zip ? ` ${selectedJob.delivery_zip}` : ''}`
                              : (selectedJob.job_location || 'N/A');
                            
                            return (
                              <>
                                {/* Main Job Delivery */}
                                <Box sx={{ mb: hasSubJobs ? 2 : 0, pb: hasSubJobs ? 1.5 : 0, borderBottom: hasSubJobs ? '1px solid' : 'none', borderColor: 'divider' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Main Job Delivery
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    <strong>Address:</strong> {mainJobAddress}
                                  </Typography>
                                  {selectedJob.delivery_notes && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      <strong>Delivery Notes:</strong> {selectedJob.delivery_notes}
                                    </Typography>
                                  )}
                                  {(selectedJob.delivered_diesel > 0 || selectedJob.delivered_def > 0) && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
                                      Ordered: {orderedDieselQty > 0 && `${orderedDieselQty} gal Diesel`} {orderedDieselQty > 0 && orderedDefQty > 0 && '/ '} {orderedDefQty > 0 && `${orderedDefQty} gal DEF`}
                                      {' → '}
                                      Delivered: {selectedJob.delivered_diesel > 0 && `${selectedJob.delivered_diesel} gal Diesel`} {selectedJob.delivered_diesel > 0 && selectedJob.delivered_def > 0 && '/ '} {selectedJob.delivered_def > 0 && `${selectedJob.delivered_def} gal DEF`}
                                    </Typography>
                                  )}
                                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                                    {mainJobDiesel > 0 && (
                                      <Typography variant="caption">
                                        <strong>Diesel:</strong> {mainJobDiesel.toFixed(2)} gal
                                        {mainDieselPrice && ` × $${currentPricing.diesel_price_per_liter}/gal = $${mainDieselPrice}`}
                                      </Typography>
                                    )}
                                    {mainJobDef > 0 && (
                                      <Typography variant="caption">
                                        <strong>DEF:</strong> {mainJobDef.toFixed(2)} gal
                                        {mainDefPrice && ` × $${currentPricing.def_price_per_liter}/gal = $${mainDefPrice}`}
                                      </Typography>
                                    )}
                                    {mainTotalFuelPrice && (mainJobDiesel > 0 && mainJobDef > 0) && (
                                      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                        Sub-total: ${mainTotalFuelPrice}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </>
                            );
                          })()}
                          
                          {/* Display sub-jobs if they exist */}
                          {selectedJob.delivery_completion && selectedJob.delivery_completion.sub_jobs && Array.isArray(selectedJob.delivery_completion.sub_jobs) && selectedJob.delivery_completion.sub_jobs.length > 0 && (
                            <>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 1.5, color: 'info.dark' }}>
                                Sub-Deliveries ({selectedJob.delivery_completion.sub_jobs.length})
                              </Typography>
                          {selectedJob.delivery_completion.sub_jobs.map((subJob, subIndex) => {
                            const subJobAddress = [
                              subJob.delivery_address,
                              subJob.delivery_city,
                              subJob.delivery_state,
                              subJob.delivery_zip
                            ].filter(Boolean).join(', ');
                            
                            const subDieselQty = parseFloat(subJob.diesel_delivered) || 0;
                            const subDefQty = parseFloat(subJob.def_delivered) || 0;
                            const subDieselPrice = currentPricing && subDieselQty > 0 
                              ? (subDieselQty * parseFloat(currentPricing.diesel_price_per_liter)).toFixed(2)
                              : null;
                            const subDefPrice = currentPricing && subDefQty > 0
                              ? (subDefQty * parseFloat(currentPricing.def_price_per_liter)).toFixed(2)
                              : null;
                            const subTotalFuelPrice = subDieselPrice && subDefPrice
                              ? (parseFloat(subDieselPrice) + parseFloat(subDefPrice)).toFixed(2)
                              : subDieselPrice || subDefPrice || null;
                            
                            return (
                              <Box key={subJob.id || subIndex} sx={{ mb: 1.5, pb: 1.5, borderBottom: subIndex < selectedJob.delivery_completion.sub_jobs.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  Delivery Stop {subIndex + 1}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  <strong>Address:</strong> {subJobAddress || 'N/A'}
                                </Typography>
                                {subJob.delivery_notes && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    <strong>Delivery Notes:</strong> {subJob.delivery_notes}
                                  </Typography>
                                )}
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                                  {subDieselQty > 0 && (
                                    <Typography variant="caption">
                                      <strong>Diesel:</strong> {subDieselQty} gal
                                      {subDieselPrice && ` × $${currentPricing.diesel_price_per_liter}/gal = $${subDieselPrice}`}
                                    </Typography>
                                  )}
                                  {subDefQty > 0 && (
                                    <Typography variant="caption">
                                      <strong>DEF:</strong> {subDefQty} gal
                                      {subDefPrice && ` × $${currentPricing.def_price_per_liter}/gal = $${subDefPrice}`}
                                    </Typography>
                                  )}
                                  {subTotalFuelPrice && (subDieselQty > 0 && subDefQty > 0) && (
                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                      Sub-total: ${subTotalFuelPrice}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                          
                          {/* Total for all deliveries (main + sub-jobs) */}
                          {(() => {
                            // Calculate main job totals
                            let subJobDieselTotal = 0;
                            let subJobDefTotal = 0;
                            selectedJob.delivery_completion.sub_jobs.forEach(subJob => {
                              subJobDieselTotal += parseFloat(subJob.diesel_delivered) || 0;
                              subJobDefTotal += parseFloat(subJob.def_delivered) || 0;
                            });
                            
                            const mainJobDiesel = Math.max(0, parseFloat(selectedJob.delivery_completion.diesel_delivered || 0) - subJobDieselTotal);
                            const mainJobDef = Math.max(0, parseFloat(selectedJob.delivery_completion.def_delivered || 0) - subJobDefTotal);
                            
                            // Total = main + sub-jobs
                            const totalDiesel = mainJobDiesel + subJobDieselTotal;
                            const totalDef = mainJobDef + subJobDefTotal;
                            
                            const totalDieselPrice = currentPricing && totalDiesel > 0 
                              ? (totalDiesel * parseFloat(currentPricing.diesel_price_per_liter)).toFixed(2)
                              : null;
                            const totalDefPrice = currentPricing && totalDef > 0
                              ? (totalDef * parseFloat(currentPricing.def_price_per_liter)).toFixed(2)
                              : null;
                            const totalFuelPrice = totalDieselPrice && totalDefPrice
                              ? (parseFloat(totalDieselPrice) + parseFloat(totalDefPrice)).toFixed(2)
                              : totalDieselPrice || totalDefPrice || null;
                            
                            return (
                              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '2px solid', borderColor: 'success.main', bgcolor: 'success.light', borderRadius: 1, p: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'success.dark' }}>
                                  Total for All Deliveries (Main + Sub-Deliveries):
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                  {totalDiesel > 0 && (
                                    <Typography variant="body2">
                                      <strong>Diesel:</strong> {totalDiesel.toFixed(2)} gal
                                      {totalDieselPrice && ` = $${totalDieselPrice}`}
                                    </Typography>
                                  )}
                                  {totalDef > 0 && (
                                    <Typography variant="body2">
                                      <strong>DEF:</strong> {totalDef.toFixed(2)} gal
                                      {totalDefPrice && ` = $${totalDefPrice}`}
                                    </Typography>
                                  )}
                                  {totalFuelPrice && (
                                    <Typography variant="body2" sx={{ ml: 'auto', fontWeight: 'bold' }}>
                                      Grand Total: ${totalFuelPrice}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          })()}
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
                
                {formData.customer && (() => {
                  const customerId = formData.customer;
                  const invoicedJobIds = getInvoicedJobIds();
                  const availableJobs = jobs.filter((job) => {
                    const jobCustomerId = typeof job.customer === 'string' 
                      ? job.customer 
                      : job.customer?.id || job.customer;
                    const isCustomerMatch = String(jobCustomerId) === String(customerId);
                    const isCompleted = job.job_status === 'complete';
                    const isNotInvoiced = !invoicedJobIds.has(job.id);
                    return isCustomerMatch && isCompleted && isNotInvoiced;
                  });
                  
                  if (availableJobs.length === 0) {
                    return (
                      <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                        No completed jobs available for this customer
                      </Typography>
                    );
                  }
                  return null;
                })()}
              </Box>

              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                {(() => {
                  const deliveryFeesTotal = formData.jobs.reduce((sum, j) => sum + (parseFloat(j.delivery_fee) || 0), 0);
                  let fuelCostsTotal = 0;
                  if (currentPricing) {
                    formData.jobs.forEach((jobItem) => {
                      const selectedJob = jobs.find(j => j.id === jobItem.job);
                      if (selectedJob) {
                        const dieselQty = parseFloat(selectedJob.delivered_diesel) || parseFloat(selectedJob.diesel_quantity) || 0;
                        const defQty = parseFloat(selectedJob.delivered_def) || parseFloat(selectedJob.def_quantity) || 0;
                        if (dieselQty > 0) {
                          fuelCostsTotal += dieselQty * parseFloat(currentPricing.diesel_price_per_liter);
                        }
                        if (defQty > 0) {
                          fuelCostsTotal += defQty * parseFloat(currentPricing.def_price_per_liter);
                        }
                      }
                    });
                  }
                  const tax = parseFloat(formData.tax) || 0;
                  const subtotal = deliveryFeesTotal + fuelCostsTotal;
                  const base = subtotal + tax;
                  const pctRaw = parseFloat(formData.credit_card_surcharge_percent);
                  const surchargePct = Number.isFinite(pctRaw) && pctRaw >= 0 ? pctRaw : 3;
                  const surcharge = Math.round(base * (surchargePct / 100) * 100) / 100;
                  const total = base + surcharge;
                  
                  return (
                    <>
                      {fuelCostsTotal > 0 && (
                        <Typography variant="body2" color="textSecondary">
                          Fuel Costs: ${fuelCostsTotal.toFixed(2)}
                        </Typography>
                      )}
                      <Typography variant="body2" color="textSecondary">
                        Delivery Fees: ${deliveryFeesTotal.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Subtotal: ${subtotal.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Tax: ${tax.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Credit card surcharge ({surchargePct.toFixed(2)}%): ${surcharge.toFixed(2)}
                      </Typography>
                      <Typography variant="h6" sx={{ mt: 1 }}>
                        Total: ${total.toFixed(2)}
                      </Typography>
                    </>
                  );
                })()}
              </Box>

              <TextField
                name="tax"
                label="Tax"
                type="number"
                value={formData.tax}
                onChange={handleChange}
                fullWidth
                inputProps={{ step: '0.01', min: '0' }}
              />

              <TextField
                name="credit_card_surcharge_percent"
                label="Credit card surcharge (%)"
                type="number"
                value={formData.credit_card_surcharge_percent}
                onChange={handleChange}
                fullWidth
                helperText="Applied to subtotal + tax. Default 3%."
                inputProps={{ step: '0.01', min: '0', max: '100' }}
              />

              <TextField
                name="due_date"
                label="Due Date"
                type="date"
                value={formData.due_date}
                onChange={handleChange}
                required
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              Create
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default ManagerInvoices;



